import React, { FC, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Button } from 'react-bootstrap'
import BootstrapTable from 'react-bootstrap-table-next'
import ToolkitProvider from 'react-bootstrap-table2-toolkit'
import CircularProgress from '@material-ui/core/CircularProgress'
import BackgroundCheckVerificationModal from '../../../components/Modals/BackgroundCheckVerificationModal'
import ServiceVerificationAuditModal from '../../../components/Modals/ServiceVerificationAuditModal'
import Status from '../Status'
import CryptoJS from 'crypto-js'
import { formatUTCDate } from '../../../utils/stringTools'
import RootState from '../../../types/RootState'
import User from '../../../types/User'
import { addUserActivity } from '../../../redux/actions/authentication'
import {
  getBackgroundCheckVerificationById, updateBackgroundCheckVerification, getServiceVerificationAuditById, updateServiceVerificationAudit
} from '../../../redux/actions/backgroundCheckVerification'

import './index.css'

interface Props {
  height: number;
  onShowFileAudit: (id: number) => void;
}

const renderActionButton = (cellContent: any, row: any, user: User, dispatch: any, setShowBackgroundCheckVerificationModal: (show: boolean) => void, setReadOnlyModal: (show: boolean) => void, onShowFileAudit: (id: number) => void, setShowServiceVerificationAuditModal: (show: boolean) => void) => {
  const emailKey = 'lakelandproviderportalinsertuserencryption'
  const email = user.isLakeland ? { eid: CryptoJS.AES.encrypt(user.email, emailKey).toString() } : JSON.parse(localStorage.getItem('p_sid')|| '{}')
  const token = localStorage.getItem('token') !== null && localStorage.getItem('token') !== 'undefined' ? JSON.parse(localStorage.getItem('token')|| '') : ''
  
  const onOpenBackgroundCheck = () => {
    dispatch(getBackgroundCheckVerificationById(row.id, token))
    setShowBackgroundCheckVerificationModal(true)
    setReadOnlyModal(row.type === 'Background Check' && row.status !== 'Follow-up needed')
    const userActivity = {
      eid: email.eid,
      activity: 'View',
      backgroundCheckVerificationId: row.id
    }
    dispatch(addUserActivity(userActivity, token))
  }

  const onOpenServiceVerification = () => {
    dispatch(getServiceVerificationAuditById(row.id, token))
    setShowServiceVerificationAuditModal(true)
    setReadOnlyModal(row.type === 'Service Verification' && row.status !== 'Follow-up needed')
    const userActivity = {
      eid: email.eid,
      activity: 'View',
      serviceVerificationAuditId: row.id
    }
    dispatch(addUserActivity(userActivity, token))
  }

  if ( row.type === 'Background Check' ) {
    if ( row.status === 'Follow-up needed' ) {
      return <Button onClick={onOpenBackgroundCheck} variant='info' >View / Edit</Button>
    } else if ( row.status !== 'Approved' ) {
      return <Button onClick={onOpenBackgroundCheck} variant='info' >View</Button>
    }
  } else if ( row.type === 'File Audit' ) {
    if ( row.status === 'Follow-up needed' ) {
      return <Button onClick={() => onShowFileAudit(row.id)} variant='info' >View / Edit</Button>
    }
  } else if ( row.type === 'Service Verification' ) {
    if ( row.status === 'Follow-up needed' ) {
      return <Button onClick={onOpenServiceVerification} variant='info' >View / Edit</Button>
    } else if ( row.status !== 'Approved' ) {
      return <Button onClick={onOpenServiceVerification} variant='info' >View</Button>
    }
  } 

  return null
}

const AuditVerification: FC<Props> = ({ height, onShowFileAudit }: Props) => {
  const dispatch = useDispatch()

  const [updatingBackgroundCheckVerification, setUpdatingBackgroundCheckVerification] = useState(false)
  const [showBackgroundCheckVerificationModal, setShowBackgroundCheckVerificationModal] = useState(false)
  const [updatingServiceVerificationAudit, setUpdatingServiceVerificationAudit] = useState(false)
  const [showServiceVerificationAuditModal, setShowServiceVerificationAuditModal] = useState(false)
  const [readOnlyModal, setReadOnlyModal] = useState(false)

  const { auditsAndVerifications, loading, updateBackgroundCheckVerificationState, updateServiceVerificationAuditState, selectedProvider, user } = useSelector((state: RootState) => ({
    auditsAndVerifications: state.backgroundCheckVerification.all.data,
    loading: state.backgroundCheckVerification.all.loading,
    updateBackgroundCheckVerificationState: state.backgroundCheckVerification.updateBackgroundCheckVerification,
    updateServiceVerificationAuditState: state.backgroundCheckVerification.updateServiceVerificationAudit,
    selectedProvider: state.authentication.selectedProvider,
    user: state.authentication.user,
  }))

  const data = loading === false ? (<div>No data available</div>) : (
    <div className='organization-circular-progress'>
      <CircularProgress size={40} />
      <span>Loading data</span>
    </div>
  )

  const handleSubmit = (values: any) => {
    const token = localStorage.getItem('token') !== null && localStorage.getItem('token') !== 'undefined' ? JSON.parse(localStorage.getItem('token')|| '') : ''
    const key = 'lakelandproviderportalinsertuserencryption'
    const email = user.isLakeland ? { eid: CryptoJS.AES.encrypt(user.email, key).toString() } : JSON.parse(localStorage.getItem('p_sid')|| '{}')
    const {
      backgroundCheckVerificationEmployee1, bidDate1, dojDate1, dhsFile1, dojFile1, bidFile1, comments1, removeDhsFile1, removeDojFile1, removeBidFile1,
      backgroundCheckVerificationEmployee2, bidDate2, dojDate2, dhsFile2, dojFile2, bidFile2, comments2, removeDhsFile2, removeDojFile2, removeBidFile2,
      backgroundCheckVerificationEmployee3, bidDate3, dojDate3, dhsFile3, dojFile3, bidFile3, comments3, removeDhsFile3, removeDojFile3, removeBidFile3,
      backgroundCheckVerificationEmployee4, bidDate4, dojDate4, dhsFile4, dojFile4, bidFile4, comments4, removeDhsFile4, removeDojFile4, removeBidFile4,
    } = values

    const data = {
      providerId: selectedProvider.provider_id,
      backgroundCheckVerificationEmployee1, bidDate1, dojDate1, dhsFile1, dojFile1, bidFile1, comments1, removeDhsFile1, removeDojFile1, removeBidFile1,
      backgroundCheckVerificationEmployee2, bidDate2, dojDate2, dhsFile2, dojFile2, bidFile2, comments2, removeDhsFile2, removeDojFile2, removeBidFile2,
      backgroundCheckVerificationEmployee3, bidDate3, dojDate3, dhsFile3, dojFile3, bidFile3, comments3, removeDhsFile3, removeDojFile3, removeBidFile3,
      backgroundCheckVerificationEmployee4, bidDate4, dojDate4, dhsFile4, dojFile4, bidFile4, comments4, removeDhsFile4, removeDojFile4, removeBidFile4,
      eid: email.eid,
    }

    /*const formData = new FormData()
    Object.keys(data).forEach((key) => {
      if (key.includes('File') && !key.includes('remove')) {
        let fileName = ''
        let lastFileIndex
        const keyIndex = key.substring(key.length-1, key.length)

        if ( key.includes('dhs') ) {
          fileName = 'DHS_Background Check Document '
          lastFileIndex = values[`uploadedDhsFile${keyIndex}`].length
        } else if ( key.includes('doj') ) {
          fileName = 'DOJ Background Check Document '
          lastFileIndex = values[`uploadedDojFile${keyIndex}`].length
        } else if ( key.includes('bid') ) {
          fileName = 'Background Information Disclosure '
          lastFileIndex = values[`uploadedBidFile${keyIndex}`].length
        }
        fileName = `${fileName}Employee ${keyIndex}`

        data[key].forEach((fileItem, index) => {
          const name = fileItem.name.split('.')
          formData.append('files', fileItem, `${fileName}_${lastFileIndex + index + 1}.${name[name.length - 1]}`)
        })
      } else {
        formData.append(key, data[key])
      }
    })*/

    setUpdatingBackgroundCheckVerification(true)
    dispatch(updateBackgroundCheckVerification(data, values.backgroundCheckVerificationId, token))
  }

  const handleCloseModal = () => {
    setShowBackgroundCheckVerificationModal(false)
    setUpdatingBackgroundCheckVerification(false)
    setReadOnlyModal(false)
  }

  const handleSubmitServiceVerification = (values: any) => {
    const token = localStorage.getItem('token') !== null && localStorage.getItem('token') !== 'undefined' ? JSON.parse(localStorage.getItem('token')|| '') : ''
    const key = 'lakelandproviderportalinsertuserencryption'
    const email = user.isLakeland ? { eid: CryptoJS.AES.encrypt(user.email, key).toString() } : JSON.parse(localStorage.getItem('p_sid')|| '{}')
    const { file, uploadedFile, removeFile } = values

    const data = {
      providerId: selectedProvider.provider_id,
      file,
      uploadedFile,
      removeFile,
      eid: email.eid,
    }

    const formData = new FormData()
    Object.keys(data).forEach((key) => {
      if (key === 'file' ) {
        data[key].forEach((fileItem) => {
          formData.append('files', fileItem)
        })
      } else {
        formData.append(key, data[key])
      }
    })

    setUpdatingServiceVerificationAudit(true)
    dispatch(updateServiceVerificationAudit(formData, values.serviceVerificationAuditId, token))
  }

  const handleCloseServiceVerificationModal = () => {
    setShowServiceVerificationAuditModal(false)
    setUpdatingServiceVerificationAudit(false)
    setReadOnlyModal(false)
  }

  const columns = [{
    dataField: 'date',
    text: 'Date',
    sort: true,
    formatter: (cellContent) => ( cellContent && formatUTCDate(cellContent) ) || '',
  }, {
    dataField: 'audit',
    text: 'Audit',
    sort: true,
  }, {
    dataField: 'type',
    text: 'Type',
    sort: true,
  }, {
    dataField: 'status',
    text: 'Status',
    sort: true,
    // eslint-disable-next-line react/display-name
    formatter: (cellContent) => <Status cellContent={cellContent} />
  }, {
    dataField: 'actions',
    text: 'Actions',
    formatter: (cellContent, row) => renderActionButton(cellContent, row, user, dispatch, setShowBackgroundCheckVerificationModal, setReadOnlyModal, onShowFileAudit, setShowServiceVerificationAuditModal)
  }]

  return (
    <div style={{ height:height-213 }}> 
      <div style={{height: '100%'}}>
        <ToolkitProvider
          keyField='id'
          bootstrap4
          data={auditsAndVerifications}
          columns={columns}
          search
        >
          {
            props => (
              <div className='background-check-table' style={{width: '100%'}}>
                <BootstrapTable
                  id='backgroundCheckTable'
                  bordered={false}
                  striped
                  condensed
                  noDataIndication={() => (data)}
                  { ...props.baseProps }
                />
              </div>
            )
          }
        </ToolkitProvider>
      </div>
      <BackgroundCheckVerificationModal
        readonly={readOnlyModal}
        show={showBackgroundCheckVerificationModal}
        updateBackgroundCheckVerification={updateBackgroundCheckVerificationState}
        updatingBackgroundCheckVerification={updatingBackgroundCheckVerification}
        onSubmit={handleSubmit}
        onClose={handleCloseModal}
      />
      <ServiceVerificationAuditModal
        readonly={readOnlyModal}
        show={showServiceVerificationAuditModal}
        updateServiceVerificationAudit={updateServiceVerificationAuditState}
        updatingServiceVerificationAudit={updatingServiceVerificationAudit}
        onSubmit={handleSubmitServiceVerification}
        onClose={handleCloseServiceVerificationModal}
      />
    </div>
  )
}

export default AuditVerification